import { useMemo, useRef } from 'react';
import { Chart, setHighcharts, type ChartOptions } from '@highcharts/react';
import Highcharts from 'highcharts/es-modules/masters/highcharts.src.js';
import 'highcharts/es-modules/masters/highcharts-more.src.js';
import 'highcharts/es-modules/masters/modules/accessibility.src.js';
import 'highcharts/es-modules/masters/modules/exporting.src.js';
import 'highcharts/es-modules/masters/modules/export-data.src.js';
import 'highcharts/es-modules/masters/modules/offline-exporting.src.js';
import 'highcharts/es-modules/masters/modules/funnel.src.js';
import 'highcharts/es-modules/masters/modules/pareto.src.js';
import 'highcharts/es-modules/masters/modules/treemap.src.js';
import 'highcharts/es-modules/masters/modules/sankey.src.js';
import 'highcharts/es-modules/masters/modules/xrange.src.js';
import 'highcharts/es-modules/masters/modules/heatmap.src.js';
import 'highcharts/es-modules/masters/modules/pattern-fill.src.js';
import type { ChartThemePayload, ColorVisionMode, ThemeAppearance, VisualPoint, VisualSpec } from '@lognai/plugin-analytics';
import { useTheme } from '@lognai/synapse-design-system-v2';
import { compileChartTheme, DEFAULT_UI_CHART_THEME, themedGradient, themedPointColor } from './chart-theme';

setHighcharts(Highcharts);

const formatValue = (value: number, unit: string): string => {
  if (unit === 'currency') return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', notation: 'compact' }).format(value);
  if (unit === 'percent') return `${value.toFixed(1)}%`;
  if (unit === 'days') return `${value.toFixed(1)} days`;
  return new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 }).format(value);
};

const formatCategory = (value: string): string => {
  if (!/^\d{4}-\d{2}-\d{2}(?:T|$)/.test(value)) return value.replace(/_/g, ' ');
  const normalized = /T/.test(value) && !/(?:Z|[+-]\d{2}:?\d{2})$/.test(value) ? `${value}Z` : value;
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return value;
  const isMonth = date.getUTCDate() === 1;
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    ...(isMonth ? { year: 'numeric' as const } : { day: 'numeric' as const }),
    timeZone: 'UTC',
  }).format(date);
};

export interface DrillSelection {
  member: string;
  point: VisualPoint;
}

interface Props {
  visual: VisualSpec;
  onSelect?: (selection: DrillSelection) => void;
  height?: number;
  compact?: boolean;
  theme?: ChartThemePayload;
  appearance?: ThemeAppearance;
  colorVisionMode?: ColorVisionMode;
}

export function HighchartsRenderer({ visual, onSelect, height = 360, compact = false, theme = DEFAULT_UI_CHART_THEME, appearance, colorVisionMode = 'normal' }: Props) {
  const { theme: appTheme } = useTheme();
  const resolvedAppearance = appearance ?? appTheme;
  const onSelectRef = useRef(onSelect);
  const categoryMemberRef = useRef(visual.categoryMember);
  onSelectRef.current = onSelect;
  categoryMemberRef.current = visual.categoryMember;

  const options = useMemo<ChartOptions>(() => {
    const reducedMotion = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const compiled = compileChartTheme(theme, resolvedAppearance, visual.template, reducedMotion, colorVisionMode);
    const tokens = compiled.tokens;
    const COLORS = compiled.colors;
    const patternMode = compiled.patternMode === 'auto' && colorVisionMode !== 'normal' ? 'always' : compiled.patternMode;
    const gradient = (from: string) => themedGradient(Highcharts, from, tokens.surface.plotBackground, tokens.series.gradientDirection, tokens.series.fillStyle);
    const primary = visual.series[0];
    const categories = primary?.points.map((point) => formatCategory(point.label)) ?? [];
    const plottedValues = visual.series.flatMap((series) => series.points.map((item) => item.value));
    const allValuesZero = plottedValues.length > 0 && plottedValues.every((value) => value === 0);
    const selectable = (item: VisualPoint, index: number) => ({
      className: `analytics-chart-point-${index}`,
      custom: { source: item },
      events: {
        click() {
          const member = categoryMemberRef.current;
          if (member) onSelectRef.current?.({ member, point: item });
        },
      },
    });
    const point = (item: VisualPoint, index: number): Highcharts.PointOptionsObject => ({
      name: formatCategory(item.label),
      y: item.value,
      color: themedPointColor(COLORS[index % COLORS.length]!, index, patternMode),
      ...selectable(item, index),
    });
    const base: ChartOptions = {
      chart: {
        backgroundColor: tokens.surface.chartBackground,
        plotBackgroundColor: tokens.surface.plotBackground,
        height,
        spacing: compact ? [5, 4, 3, 4] : [tokens.series.spacing + 10, tokens.series.spacing + 8, tokens.series.spacing + 2, tokens.series.spacing],
        animation: compiled.animation,
        style: { fontFamily: compiled.fontFamily },
      },
      title: { text: undefined },
      credits: { enabled: false },
      lang: { accessibility: { chartContainerLabel: '{title}. Interactive chart.' } },
      colors: COLORS,
      legend: {
        ...compiled.legend,
        enabled: !compact && compiled.legend.enabled,
        itemStyle: { color: tokens.surface.text, fontWeight: String(tokens.typography.labelWeight), fontSize: compact ? '10px' : compiled.fontSizes.legend },
        itemDistance: compact ? 8 : 20,
        symbolWidth: compact ? 10 : 16,
      },
      accessibility: { enabled: true, description: visual.title },
      exporting: {
        enabled: !compact,
        fallbackToExportServer: false,
        buttons: {
          contextButton: {
            symbolStroke: tokens.surface.mutedText,
            symbolStrokeWidth: 1.5,
            theme: { fill: 'transparent', stroke: 'transparent' },
            menuItems: ['downloadPNG', 'downloadJPEG', 'downloadSVG', 'separator', 'downloadCSV'],
          },
        },
      },
      tooltip: {
        shared: visual.template === 'area' || visual.template === 'stacked_area' || visual.template === 'stacked_column',
        useHTML: true,
        borderWidth: 0,
        shadow: tokens.components.tooltipStyle === 'elevated',
        backgroundColor: tokens.surface.tooltipBackground,
        style: { color: tokens.surface.tooltipText },
        formatter() {
          const points = this.points ?? [this];
          const body = points.map((entry) => {
            const series = visual.series.find((candidate) => candidate.name === entry.series.name) ?? primary;
            const value = visual.template === 'diverging_bar' ? Math.abs(entry.y ?? 0) : entry.y ?? 0;
            return `<div style="display:flex;justify-content:space-between;gap:24px"><span>${entry.series.name}</span><b>${formatValue(value, series?.unit ?? 'number')}</b></div>`;
          }).join('');
          return `<div style="min-width:180px;color:${tokens.surface.tooltipText}"><b>${formatCategory(String(this.key ?? ''))}</b>${body}</div>`;
        },
      },
      xAxis: { categories, lineColor: tokens.surface.axis, tickColor: tokens.surface.axis, labels: { style: { color: tokens.surface.mutedText, fontSize: compact ? '10px' : compiled.fontSizes.axis, textOverflow: 'ellipsis' } } },
      yAxis: { min: 0, max: allValuesZero ? 1 : undefined, startOnTick: false, endOnTick: false, title: { text: undefined }, gridLineColor: tokens.surface.grid, gridLineWidth: compiled.gridLineWidth, labels: { style: { color: tokens.surface.mutedText, fontSize: compact ? '10px' : compiled.fontSizes.axis, textOverflow: 'ellipsis' } } },
      plotOptions: {
        series: {
          animation: compiled.animation,
          cursor: onSelect ? 'pointer' : 'default',
          borderWidth: tokens.series.borderWidth,
          opacity: tokens.series.opacity,
          dataLabels: {
            enabled: compiled.dataLabelsEnabled,
            formatter() {
              const series = visual.series.find((candidate) => candidate.name === this.series.name) ?? primary;
              return formatValue(this.y ?? 0, series?.unit ?? 'number');
            },
            style: { color: tokens.surface.text, fontSize: compiled.fontSizes.data, fontWeight: String(tokens.typography.labelWeight), textOutline: 'none' },
          },
          states: { hover: { brightness: tokens.interaction.hoverBrightness }, inactive: { opacity: tokens.interaction.inactiveOpacity } },
        },
        bar: { borderRadius: tokens.series.borderRadius },
        column: { borderRadius: tokens.series.borderRadius },
      },
    };

    if (!primary) return base;
    if (visual.template === 'area') {
      base.series = visual.series.map((series, index) => ({
        type: 'areaspline',
        name: series.name,
        data: series.points.map(point),
        color: COLORS[index % COLORS.length],
        fillColor: gradient(COLORS[index % COLORS.length]),
        lineWidth: tokens.series.lineWidth,
        marker: { enabled: tokens.series.marker !== 'none', symbol: tokens.series.marker === 'none' ? 'circle' : tokens.series.marker, states: { hover: { enabled: true, radius: 5 } } },
      }));
    } else if (visual.template === 'stacked_area') {
      base.plotOptions = { ...base.plotOptions, area: { stacking: 'normal', marker: { enabled: false } } };
      base.series = visual.series.map((series, index) => ({
        type: 'area',
        name: series.name,
        data: series.points.map(point),
        color: COLORS[index % COLORS.length],
        fillColor: gradient(COLORS[index % COLORS.length]),
        lineWidth: tokens.series.lineWidth,
      }));
    } else if (visual.template === 'horizontal_bar') {
      base.chart = { ...base.chart, type: 'bar', height: compact ? height : Math.max(320, categories.length * 38) };
      base.series = visual.series.map((series, index) => ({
        type: 'bar', name: series.name, data: series.points.map(point), color: gradient(COLORS[index % COLORS.length]!),
      }));
    } else if (visual.template === 'diverging_bar') {
      base.chart = { ...base.chart, type: 'bar', height: compact ? height : Math.max(360, categories.length * 42) };
      base.yAxis = {
        title: { text: undefined },
        gridLineColor: tokens.surface.grid,
        gridLineWidth: compiled.gridLineWidth,
        labels: { formatter() { return new Intl.NumberFormat('en-US', { notation: 'compact' }).format(Math.abs(Number(this.value))); } },
        plotLines: [{ value: 0, color: tokens.surface.axis, width: 1, zIndex: 4 }],
      };
      base.plotOptions = { ...base.plotOptions, bar: { grouping: false, pointPadding: 0.13 } };
      base.series = visual.series.map((series, index) => ({
        type: 'bar',
        name: series.name,
        data: series.points.map((item, pointIndex) => ({
          ...point(item, pointIndex),
          y: index === 0 ? -item.value : item.value,
        })),
        color: index === 0 ? tokens.palette.diverging.negative : tokens.palette.diverging.positive,
      }));
    } else if (visual.template === 'stacked_column') {
      base.plotOptions = { ...base.plotOptions, column: { stacking: 'normal' } };
      base.series = visual.series.map((series, index) => ({
        type: 'column', name: series.name, data: series.points.map(point), color: gradient(COLORS[index % COLORS.length]!),
      }));
    } else if (visual.template === 'donut') {
      base.series = [{
        type: 'pie', name: primary.name, innerSize: '58%', data: primary.points.map(point),
        dataLabels: compact
          ? { enabled: true, format: '<b>{point.percentage:.0f}%</b>', distance: -22, style: { color: tokens.surface.tooltipText, fontSize: '9px', textOutline: 'none' } }
          : { enabled: tokens.components.labelDensity !== 'minimal', format: '{point.name}<br><b>{point.percentage:.0f}%</b>', distance: 14, style: { color: tokens.surface.text, textOutline: 'none' } },
      }];
    } else if (visual.template === 'treemap') {
      const treemapPoints = primary.points.filter((item) => Number.isFinite(item.value) && item.value > 0);
      base.chart = { ...base.chart, type: 'treemap', height, spacing: compact ? [3, 3, 3, 3] : [8, 8, 8, 8] };
      base.xAxis = { visible: false };
      base.yAxis = { visible: false };
      base.legend = { enabled: false };
      base.tooltip = {
        useHTML: true,
        borderWidth: 0,
        formatter() {
          return `<div style="min-width:150px"><b>${String(this.name ?? '')}</b><br>${formatValue(Number(this.value ?? 0), primary.unit)}</div>`;
        },
      };
      base.series = [{
        type: 'treemap',
        name: primary.name,
        layoutAlgorithm: 'squarified',
        alternateStartingDirection: true,
        colorByPoint: true,
        data: treemapPoints.map((item, index) => ({
          name: formatCategory(item.label),
          value: item.value,
          color: themedPointColor(COLORS[index % COLORS.length]!, index, patternMode),
          ...selectable(item, index),
        })),
        dataLabels: {
          enabled: true,
          format: compact ? '<b>{point.name}</b>' : '<b>{point.name}</b><br>{point.value:,.0f}',
          crop: true,
          overflow: 'justify',
          padding: compact ? 2 : 5,
          style: { color: tokens.surface.tooltipText, textOutline: 'none', fontWeight: String(tokens.typography.labelWeight), ...(compact ? { fontSize: '9px' } : {}) },
        },
      }];
    } else if (visual.template === 'sankey') {
      base.chart = { ...base.chart, height: compact ? height : 390 };
      base.series = [{
        type: 'sankey',
        name: primary.name,
        keys: ['from', 'to', 'weight'],
        data: primary.points.map((item, index) => ({
          from: item.source ?? 'Unknown status',
          to: item.target ?? 'Unknown blocker',
          weight: item.value,
          ...selectable(item, index),
        })),
        nodeWidth: 18,
        nodePadding: 22,
        dataLabels: { enabled: tokens.components.labelDensity !== 'minimal', style: { color: tokens.surface.text, textOutline: 'none', fontWeight: String(tokens.typography.labelWeight), ...(compact ? { fontSize: '9px' } : {}) } },
      }];
    } else if (visual.template === 'funnel') {
      base.series = [{
        type: 'funnel', name: primary.name, data: primary.points.map(point), neckWidth: '32%', neckHeight: '22%', width: '78%',
        dataLabels: compact
          ? { enabled: true, softConnector: false, distance: 3, format: '{point.name}: {point.y:,.0f}', style: { fontSize: '9px', textOutline: 'none' } }
          : { enabled: true, softConnector: true, format: '<b>{point.name}</b>: {point.y:,.0f}' },
      }];
    } else if (visual.template === 'pareto') {
      base.chart = { ...base.chart, height: compact ? height : Math.max(360, categories.length * 34) };
      base.yAxis = [
        { title: { text: primary.name }, gridLineColor: tokens.surface.grid, gridLineWidth: compiled.gridLineWidth, labels: { style: { color: tokens.surface.mutedText } } },
        { title: { text: 'Cumulative share' }, max: 100, min: 0, opposite: true, labels: { format: '{value}%' } },
      ];
      base.series = [
        { type: 'column', id: 'primary', name: primary.name, data: primary.points.map(point), color: gradient(COLORS[0]!) },
        { type: 'pareto', name: 'Cumulative share', baseSeries: 'primary', yAxis: 1, color: tokens.palette.comparison.target, zIndex: 3 },
      ];
    } else if (visual.template === 'bubble') {
      const allXZero = primary.points.every((item) => (item.x ?? 0) === 0);
      const allYZero = primary.points.every((item) => (item.y ?? 0) === 0);
      base.xAxis = { min: 0, max: allXZero ? 1 : undefined, startOnTick: false, endOnTick: false, title: { text: visual.series[0]?.name }, gridLineColor: tokens.surface.grid, gridLineWidth: compiled.gridLineWidth, labels: { style: { color: tokens.surface.mutedText } } };
      base.yAxis = { min: 0, max: allYZero ? 1 : undefined, startOnTick: false, endOnTick: false, title: { text: visual.series[1]?.name }, gridLineColor: tokens.surface.grid, gridLineWidth: compiled.gridLineWidth, labels: { style: { color: tokens.surface.mutedText } } };
      base.series = [{
        type: 'bubble',
        name: 'Supplier risk',
        minSize: 12,
        maxSize: 64,
        data: primary.points.map((item, index) => ({
          x: item.x, y: item.y, z: item.z, name: formatCategory(item.label), color: Highcharts.color(COLORS[index % COLORS.length]!).setOpacity(tokens.series.opacity).get(),
          ...selectable(item, index),
        })),
        dataLabels: { enabled: true, format: '{point.name}', allowOverlap: false },
      }];
    } else if (visual.template === 'gantt') {
      const ganttPoints = primary.points.filter((item) => item.x != null && item.x2 != null);
      base.chart = { ...base.chart, type: 'xrange', height: compact ? height : Math.max(360, ganttPoints.length * 34) };
      base.xAxis = { type: 'datetime', title: { text: undefined }, gridLineColor: tokens.surface.grid, gridLineWidth: compiled.gridLineWidth, labels: { style: { color: tokens.surface.mutedText } } };
      base.yAxis = {
        categories: ganttPoints.map((item) => formatCategory(item.label)),
        title: { text: undefined },
        reversed: true,
      };
      base.tooltip = {
        useHTML: true,
        formatter() {
          const start = Highcharts.dateFormat('%b %e, %Y', Number(this.x));
          const end = Highcharts.dateFormat('%b %e, %Y', Number((this as Highcharts.Point & { x2?: number }).x2));
          return `<b>${String(this.name ?? '')}</b><br>${start} → ${end}`;
        },
      };
      base.series = [{
        type: 'xrange',
        name: 'Arrival window',
        pointWidth: 14,
        borderRadius: 5,
        data: ganttPoints.map((item, index) => ({
          x: item.x!,
          x2: item.x2!,
          y: index,
          name: formatCategory(item.label),
          color: themedPointColor(COLORS[index % COLORS.length]!, index, patternMode),
          ...selectable(item, index),
        })),
      }];
    } else if (visual.template === 'heatmap') {
      const ageOrder = ['0-7 days', '8-30 days', '31-60 days', '61-90 days', '91+ days', 'Unknown'];
      const priorityOrder = ['urgent', 'high', 'normal', 'low', 'Unknown'];
      const xValues = [...new Set(primary.points.map((item) => item.xLabel ?? 'Unknown'))];
      const yValues = [...new Set(primary.points.map((item) => item.yLabel ?? 'Unknown'))];
      const xCategories = [...ageOrder.filter((item) => xValues.includes(item)), ...xValues.filter((item) => !ageOrder.includes(item))];
      const yCategories = [...priorityOrder.filter((item) => yValues.includes(item)), ...yValues.filter((item) => !priorityOrder.includes(item))];
      base.chart = { ...base.chart, type: 'heatmap', height: compact ? height : Math.max(340, yCategories.length * 64) };
      base.xAxis = { categories: xCategories, title: { text: compact ? undefined : 'Order age' }, lineColor: tokens.surface.axis, labels: { style: { color: tokens.surface.mutedText, fontSize: compact ? '9px' : compiled.fontSizes.axis } } };
      base.yAxis = { categories: yCategories.map(formatCategory), title: { text: compact ? undefined : 'Priority' }, reversed: true, labels: { style: { color: tokens.surface.mutedText, fontSize: compact ? '9px' : compiled.fontSizes.axis } } };
      base.colorAxis = { min: 0, minColor: tokens.palette.sequential.low, maxColor: tokens.palette.sequential.high };
      base.series = [{
        type: 'heatmap',
        name: primary.name,
        data: primary.points.map((item, index) => ({
          x: xCategories.indexOf(item.xLabel ?? 'Unknown'),
          y: yCategories.indexOf(item.yLabel ?? 'Unknown'),
          value: item.value,
          ...selectable(item, index),
        })),
        dataLabels: { enabled: true, format: '{point.value:,.0f}', style: { textOutline: 'none', fontWeight: '600' } },
      }];
    } else if (visual.template === 'calendar_heatmap') {
      const dated = primary.points
        .filter((item) => item.x != null)
        .sort((a, b) => (a.x ?? 0) - (b.x ?? 0));
      const first = dated[0]?.x ?? Date.now();
      const firstDate = new Date(first);
      const mondayOffset = (firstDate.getUTCDay() + 6) % 7;
      const calendarStart = Date.UTC(firstDate.getUTCFullYear(), firstDate.getUTCMonth(), firstDate.getUTCDate() - mondayOffset);
      const weekIndex = (timestamp: number) => Math.floor((timestamp - calendarStart) / 604_800_000);
      const weekCount = Math.max(1, ...dated.map((item) => weekIndex(item.x!))) + 1;
      const weekLabels = Array.from({ length: weekCount }, (_, index) => Highcharts.dateFormat('%b %e', calendarStart + index * 604_800_000));
      base.chart = { ...base.chart, type: 'heatmap', height: compact ? height : Math.max(360, weekCount * 34 + 90) };
      base.xAxis = { categories: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'], opposite: true, lineWidth: 0 };
      base.yAxis = { categories: weekLabels, title: { text: undefined }, reversed: true, gridLineWidth: 0 };
      base.colorAxis = { min: 0, minColor: tokens.palette.sequential.low, maxColor: tokens.palette.sequential.high };
      base.tooltip = {
        useHTML: true,
        formatter() {
          const source = (this.options.custom as { source?: VisualPoint } | undefined)?.source;
          return `<b>${source ? formatCategory(source.label) : ''}</b><br>${formatValue(source?.value ?? 0, primary.unit)}`;
        },
      };
      base.series = [{
        type: 'heatmap',
        name: primary.name,
        borderWidth: 3,
        borderColor: tokens.surface.chartBackground,
        data: dated.map((item, index) => {
          const date = new Date(item.x!);
          return {
            x: (date.getUTCDay() + 6) % 7,
            y: weekIndex(item.x!),
            value: item.value,
            ...selectable(item, index),
          };
        }),
        dataLabels: { enabled: false },
      }];
    }
    return base;
  }, [colorVisionMode, compact, height, onSelect, resolvedAppearance, theme, visual]);

  return (
    <div>
      <Chart
        key={visual.categoryMember ?? visual.title}
        options={options}
        containerProps={{
          className: 'w-full',
          style: { minHeight: height },
          ...({ 'data-category-member': visual.categoryMember } as Record<string, string | undefined>),
        }}
      />
    </div>
  );
}
